/**
 * The Start block's panel.
 *
 * Start is not a step — a plan enters at `steps[0]`, and what makes Radar pick
 * a work order up (priorities, problem types, active window) is the Scenario,
 * a separate record. So everything here is read-only and points at the
 * Scenarios tab, which is where it can actually be changed.
 */

import React, { FC } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { AOPActiveWindow, AOPCustomWindow, AOPScenario } from '../../../types/AOPTypes';
import { AOPPanelField, AOPPanelReadOnly } from './AOPPanelShell';
import {
	activeWindowLabel,
	describePriorityScope,
	describeProblemTypeScope,
} from '../utils/AOPScenarioUtils';

const DAY_LABELS: { [day: number]: string } = {
	1: 'Mon',
	2: 'Tue',
	3: 'Wed',
	4: 'Thu',
	5: 'Fri',
	6: 'Sat',
	7: 'Sun',
};

/**
 * `24:00` is how the wire says midnight at the end of a day; moment cannot parse
 * it, so it is spelled out rather than coerced to `00:00`, which would read as
 * the start of the day instead.
 */
const formatTime = (time: string): string =>
	time === '24:00' ? 'midnight' : moment(time, 'HH:mm').format('h:mm A');

const CustomWindowGrid: FC<{ window: AOPCustomWindow }> = ({ window: activeWindow }) => (
	<div className="flex flex-col">
		{[1, 2, 3, 4, 5, 6, 7].map((day) => {
			const ranges = activeWindow.ranges.filter((range) => range.days.indexOf(day) >= 0);

			return (
				<div key={day} className="flex items-start gap-m py-2xs">
					<span className="bodyregular w-10 shrink-0 text-character-secondary">
						{DAY_LABELS[day]}
					</span>
					<span className="bodyregular text-character-primary">
						{ranges.length === 0
							? 'Closed'
							: ranges
									.map((range) => `${formatTime(range.from)} – ${formatTime(range.to)}`)
									.join(', ')}
					</span>
				</div>
			);
		})}
	</div>
);

interface AOPStartPanelProps {
	scenario: AOPScenario | null;
	scenariosHref: string;
}

export const AOPStartPanel: FC<AOPStartPanelProps> = ({ scenario, scenariosHref }) => {
	if (!scenario) {
		return (
			<span className="bodyregular text-character-secondary">
				No scenario is routing work orders to this procedure yet, so nothing reaches it.{' '}
				<Link to={scenariosHref}>Add one</Link>.
			</span>
		);
	}

	const activeWindow: AOPActiveWindow = scenario.active_window;
	const windowLabel = activeWindowLabel(activeWindow);

	return (
		<>
			<div className="flex flex-col gap-2xs">
				<span className="h5medium text-character-primary">Trigger criteria</span>
				<span className="footnoteregular text-character-secondary">
					Radar starts when a work order matches the selected criteria.
				</span>
			</div>

			<AOPPanelField label="Work order priority" required>
				<AOPPanelReadOnly>{describePriorityScope(scenario.priority_ids)}</AOPPanelReadOnly>
			</AOPPanelField>

			<AOPPanelField label="Work order problem type" required>
				<AOPPanelReadOnly>{describeProblemTypeScope(scenario.problem_type_ids)}</AOPPanelReadOnly>
			</AOPPanelField>

			<AOPPanelField label="Active window" required>
				<AOPPanelReadOnly>{windowLabel}</AOPPanelReadOnly>
			</AOPPanelField>

			{activeWindow.type === 'custom' ? (
				<CustomWindowGrid window={activeWindow as AOPCustomWindow} />
			) : null}

			<span className="bodyregular text-character-secondary">
				To edit these, go to <Link to={scenariosHref}>Scenarios</Link>.
			</span>
		</>
	);
};
